import { Component } from '@theme/component';

/**
 * Wraps a hero <video> element and its play/pause control.
 *
 * Responsibilities:
 *   - WCAG 2.2.2 Pause, Stop, Hide: provide a visible, keyboard-operable
 *     control to pause the auto-playing hero video.
 *   - WCAG 2.3.1 Three Flashes: paired with reduced-motion handling to stop
 *     motion for users who opt out.
 *   - Respect prefers-reduced-motion at mount and when the system setting
 *     changes mid-session.
 *   - Keep the button icon and aria-label in sync with the video's real
 *     play state, including when autoplay is blocked or the video ends.
 *
 * The button itself is rendered server-side in sections/hero.liquid with
 * localized labels sourced from `accessibility.pause_video` /
 * `accessibility.play_video` in locales/en.default.json and locales/fr.json.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} heroVideoToggle - The play/pause button.
 * @property {HTMLElement} heroVideoIconPlay - The play-state icon wrapper.
 * @property {HTMLElement} heroVideoIconPause - The pause-state icon wrapper.
 *
 * @extends {Component<Refs>}
 */
class HeroVideoControl extends Component {
  #reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** @type {HTMLVideoElement | null} */
  #video = null;

  /** @type {IntersectionObserver | null} */
  #visibilityObserver = null;

  /** @type {number | null} */
  #settleTimer = null;

  connectedCallback() {
    super.connectedCallback();

    this.#video = this.querySelector('video.hero__media');
    if (!this.#video) return;

    this.#video.addEventListener('play', this.#syncState);
    this.#video.addEventListener('pause', this.#syncState);
    this.#video.addEventListener('ended', this.#syncState);

    // WCAG 2.2.2 + 2.3.3 — sections/hero.liquid renders the <video> WITHOUT
    // the native `autoplay` attribute and instead marks it with
    // data-nh-autoplay="true". This avoids the brief paint flash where the
    // video would otherwise start playing for prefers-reduced-motion users
    // before our reduced-motion check could pause it.
    //
    // Autoplay is additionally gated on visibility (2026-08-04 audit). The
    // hero renders separate desktop and mobile <video> elements and hides the
    // inactive one with CSS, but an unconditional play() forces the hidden
    // element to download its entire file: the 10.7 MB mobile hero was fully
    // buffered on every desktop load (readyState 4 at 0x0 px). A display:none
    // element never intersects, so the observer starts only the visible
    // variant — and if the viewport later crosses the breakpoint, the other
    // variant intersects and starts then. Videos ship preload="none", so the
    // hidden one now costs nothing.
    //
    // Playback is additionally deferred until the page has settled (2026-09-24).
    // The hero is the first thing to intersect, so starting it on intersection
    // put a 10.26 MB download straight into the critical path and the hero sat
    // blank for seconds while it arrived. The poster frame is already painted
    // by then, so holding the film back costs the visitor nothing visually and
    // lets the rest of the page finish first.
    if (this.#video.dataset.nhAutoplay === 'true') {
      this.#visibilityObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#visibilityObserver?.unobserve(entry.target);
          this.#whenPageSettled(() => {
            // Re-check rather than trusting the value captured at schedule
            // time: the visitor may have paused it, or switched on reduced
            // motion, in the seconds we waited.
            if (this.#reducedMotionQuery.matches) return;
            if (!this.#video || !this.#video.paused) return;
            const maybePromise = this.#video.play();
            if (maybePromise && typeof maybePromise.catch === 'function') {
              // Swallow autoplay-policy / browser rejections silently — the
              // user can always start the video via the visible play button.
              maybePromise.catch(() => {});
            }
          });
        }
      });
      this.#visibilityObserver.observe(this.#video);
    }

    this.#reducedMotionQuery.addEventListener('change', this.#applyReducedMotion);

    this.#syncState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#reducedMotionQuery.removeEventListener('change', this.#applyReducedMotion);
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
    if (this.#settleTimer !== null) {
      clearTimeout(this.#settleTimer);
      this.#settleTimer = null;
    }
    if (this.#video) {
      this.#video.removeEventListener('play', this.#syncState);
      this.#video.removeEventListener('pause', this.#syncState);
      this.#video.removeEventListener('ended', this.#syncState);
    }
  }

  /**
   * Toggles the video play state. Bound declaratively via
   * `on:click="/toggleHeroVideo"` in sections/hero.liquid.
   */
  toggleHeroVideo = () => {
    if (!this.#video) return;
    if (this.#video.paused) {
      // .play() returns a promise; swallow rejections (autoplay policy, etc.)
      const maybePromise = this.#video.play();
      if (maybePromise && typeof maybePromise.catch === 'function') {
        maybePromise.catch(() => {});
      }
    } else {
      this.#video.pause();
    }
  };

  /**
   * Defers work until the page has finished loading and the main thread is
   * free, with a hard ceiling so a stalled third-party request can never leave
   * the hero frozen on its poster.
   *
   * @param {() => void} callback
   */
  #whenPageSettled = (callback) => {
    let ran = false;
    const run = () => {
      if (ran) return;
      ran = true;
      if (this.#settleTimer !== null) {
        clearTimeout(this.#settleTimer);
        this.#settleTimer = null;
      }
      callback();
    };

    this.#settleTimer = window.setTimeout(run, 3000);

    const afterLoad = () => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(run, { timeout: 800 });
      } else {
        run();
      }
    };

    if (document.readyState === 'complete') {
      afterLoad();
    } else {
      window.addEventListener('load', afterLoad, { once: true });
    }
  };

  #applyReducedMotion = () => {
    if (!this.#video) return;
    if (this.#reducedMotionQuery.matches && !this.#video.paused) {
      this.#video.pause();
    }
  };

  #syncState = () => {
    if (!this.#video) return;

    const { heroVideoToggle, heroVideoIconPlay, heroVideoIconPause } = this.refs;
    if (!heroVideoToggle) return;

    const paused = this.#video.paused || this.#video.ended;
    const pauseLabel = heroVideoToggle.dataset.pauseLabel || '';
    const playLabel = heroVideoToggle.dataset.playLabel || '';

    heroVideoToggle.setAttribute('aria-label', paused ? playLabel : pauseLabel);
    heroVideoToggle.removeAttribute('aria-pressed');

    if (heroVideoIconPause instanceof HTMLElement) {
      heroVideoIconPause.classList.toggle('hidden', paused);
    }
    if (heroVideoIconPlay instanceof HTMLElement) {
      heroVideoIconPlay.classList.toggle('hidden', !paused);
    }
  };
}

if (!customElements.get('hero-video-control')) {
  customElements.define('hero-video-control', HeroVideoControl);
}
